import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TrustCredentialsSection = () => {
  const [isVisible, setIsVisible] = useState(true); // Changed to true for mobile compatibility
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 } // Lowered threshold for better mobile detection
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const licenses = [
    {
      id: 1,
      name: "Gaming License",
      authority: "Sikkim Gaming Commission",
      number: "SG/2023/Nitin/001",
      validUntil: "December 2025",
      icon: "Shield",
      status: "Active"
    },
    {
      id: 2,
      name: "Payment License",
      authority: "Reserve Bank of India",
      number: "RBI/PL/2023/Nitin",
      validUntil: "March 2026",
      icon: "CreditCard",
      status: "Active"
    },
    {
      id: 3,
      name: "Data Protection",
      authority: "ISO 27001 Certified",
      number: "ISO27001/2023/Nitin",
      validUntil: "June 2025",
      icon: "Lock",
      status: "Active"
    }
  ];

  const partnerships = [
    {
      id: 1,
      name: "Paytm",
      type: "Payment Partner",
      logo: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=100&h=100&fit=crop&crop=center",
      description: "Secure and instant payment processing"
    },
    {
      id: 2,
      name: "PhonePe",
      type: "Payment Partner",
      logo: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=100&h=100&fit=crop&crop=center",
      description: "UPI and digital wallet integration"
    },
    {
      id: 3,
      name: "Razorpay",
      type: "Payment Gateway",
      logo: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=100&h=100&fit=crop&crop=center",
      description: "Advanced payment security solutions"
    },
    {
      id: 4,
      name: "AWS",
      type: "Cloud Partner",
      logo: "https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=100&h=100&fit=crop&crop=center",
      description: "Secure cloud infrastructure"
    }
  ];

  const securityFeatures = [
    {
      icon: "Shield",
      title: "256-bit SSL Encryption",
      description: "Bank-grade security for all transactions and personal data"
    },
    {
      icon: "Lock",
      title: "Two-Factor Authentication",
      description: "Additional security layer for account protection"
    },
    {
      icon: "Eye",
      title: "24/7 Fraud Monitoring",
      description: "Real-time monitoring and suspicious activity detection"
    },
    {
      icon: "Database",
      title: "Secure Data Storage",
      description: "Encrypted data storage with regular security audits"
    },
    {
      icon: "UserCheck",
      title: "KYC Verification",
      description: "Comprehensive identity verification for all players"
    },
    {
      icon: "Clock",
      title: "Instant Payouts",
      description: "Automated payout system with 99.9% uptime"
    }
  ];

  return (
    <section ref={sectionRef} className="py-12 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center shadow-royal">
              <Icon name="ShieldCheck" size={24} color="white" />
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-gray-900 mb-4">
            Trust & <span className="text-gradient-royal">Security Fortress</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Your security is our priority. We maintain the highest standards of compliance, security, and transparency in the gaming industry.
          </p>
        </div>

        {/* Licenses Section */}
        <div className={`mb-12 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-xl font-heading font-bold text-center text-gray-900 mb-8">
            Official <span className="text-gradient-royal">Licenses & Certifications</span>
          </h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {licenses?.map((license, index) => (
              <div
                key={license?.id}
                className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${(index + 3) * 200}ms` }}
              >
                <div className="bg-white rounded-2xl p-6 shadow-subtle border border-gray-100 hover:shadow-royal transition-all duration-300 hover-lift">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center">
                      <Icon name={license?.icon} size={24} color="white" />
                    </div>
                    <span className="px-3 py-1 bg-success/20 text-success text-xs font-bold rounded-full">
                      {license?.status}
                    </span>
                  </div>
                  
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{license?.name}</h4>
                  <p className="text-gray-600 text-sm mb-4">{license?.authority}</p>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">License No:</span>
                      <span className="text-gray-900 font-medium">{license?.number}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Valid Until:</span>
                      <span className="text-gray-900 font-medium">{license?.validUntil}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Security Features */}
        <div className={`mb-20 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-2xl font-heading font-bold text-center text-gray-900 mb-12">
            Advanced <span className="text-gradient-royal">Security Features</span>
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {securityFeatures?.map((feature, index) => (
              <div
                key={index}
                className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${(index + 6) * 150}ms` }}
              >
                <div className="bg-white rounded-xl p-6 shadow-subtle border border-gray-100 hover:shadow-royal transition-all duration-300 hover-lift">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Icon name={feature?.icon} size={24} color="var(--color-primary)" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{feature?.title}</h4>
                  <p className="text-gray-600 text-sm">{feature?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Partnerships */}
        <div className={`transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-2xl font-heading font-bold text-center text-gray-900 mb-12">
            Trusted <span className="text-gradient-royal">Partners</span>
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerships?.map((partner, index) => (
              <div
                key={partner?.id}
                className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${(index + 12) * 100}ms` }}
              >
                <div className="bg-white rounded-xl p-6 shadow-subtle border border-gray-100 hover:shadow-royal transition-all duration-300 hover-lift text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full overflow-hidden border-2 border-gray-100">
                    <Image
                      src={partner?.logo}
                      alt={partner?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">{partner?.name}</h4>
                  <p className="text-primary text-sm font-medium mb-2">{partner?.type}</p>
                  <p className="text-gray-600 text-xs">{partner?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Stats */}
        <div className={`mt-16 transition-all duration-1000 delay-900 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-8 border border-primary/20">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Shield" size={24} color="white" />
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">100%</p>
                <p className="text-gray-600 text-sm">Secure Transactions</p>
              </div>
              <div>
                <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Clock" size={24} color="white" />
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">&lt;5min</p>
                <p className="text-gray-600 text-sm">Average Payout Time</p>
              </div>
              <div>
                <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Users" size={24} color="white" />
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">50K+</p>
                <p className="text-gray-600 text-sm">Verified Players</p>
              </div>
              <div>
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Star" size={24} color="white" />
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">99.9%</p>
                <p className="text-gray-600 text-sm">Platform Uptime</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustCredentialsSection;